"use no memo";

import { LocalOrderType } from 'context/OrderContext';

import type {
  ReorderResponseV5,
  OrderDetailResponseV6,
  SubmittedOrderType,
  OrderValidateResponseV6,
  YelpOpportunityResponse,
} from 'types/order';

import {
  ApiRequestFlags,
  ORDER_DETAILS_API_VERSION,
  ORDER_SUBMIT_API_VERSION,
  ORDER_VALIDATE_API_VERSION,
  REORDER_VALIDATE_API_VERSION,
  getFromChownowApi,
  postToChownowApi,
} from './api';

type OrderApiCommonOptions = {
  controller?: AbortController;
  flags?: ApiRequestFlags;
};

export async function getOrder(
  orderId: string,
  guid?: string | null,
  { controller }: OrderApiCommonOptions = {}
) {
  return getFromChownowApi<OrderDetailResponseV6>({
    endpoint: `order/${orderId}`,
    params: {
      ...(guid && { guid }),
      time: new Date().getTime(),
    },
    apiVersion: ORDER_DETAILS_API_VERSION,
    controller,
  });
}

export async function reorder(
  orderId: SubmittedOrderType['id'],
  { controller }: OrderApiCommonOptions = {}
) {
  return postToChownowApi<ReorderResponseV5>({
    endpoint: `order/${orderId}/reorder`,
    apiVersion: REORDER_VALIDATE_API_VERSION,
    controller,
  });
}

export function submitOrder(
  // $TSFIXME: add types here when the payload is added to types/order.ts
  body: Record<string, unknown>,
  { controller, flags }: OrderApiCommonOptions = {}
) {
  // $TSFIXME: is this actually the type for this? There isn't really validation on posting to that endpoint
  return postToChownowApi<OrderDetailResponseV6>({
    endpoint: 'order',
    body,
    apiVersion: ORDER_SUBMIT_API_VERSION,
    controller,
    flags,
    // for this endpoint, we want to update the address id locally
    // even if the submit action fails to prevent duplicate addresses being created
    returnDataForErrorResponse: true,
  });
}

export function submitYelpOrder(
  // $TSFIXME: add types here when the payload is added to types/order.ts
  body: Record<string, unknown>,
  { controller, flags }: OrderApiCommonOptions = {}
) {
  return postToChownowApi<OrderDetailResponseV6>({
    endpoint: 'yelp',
    body,
    apiVersion: ORDER_SUBMIT_API_VERSION,
    controller,
    flags,
    // for this endpoint, we want to update the address id locally
    // even if the submit action fails to prevent duplicate addresses being created
    returnDataForErrorResponse: true,
  });
}

export async function updateOrderTip(orderId: string, newTip: number) {
  return postToChownowApi({
    endpoint: `order/${orderId}/adjust`,
    body: { tip: newTip },
    method: 'PATCH',
  });
}

export async function validateYelpOrder(
  body: Record<string, unknown>,
  { controller, flags }: OrderApiCommonOptions = {}
) {
  return postToChownowApi<OrderValidateResponseV6>({
    endpoint: 'yelp/validate',
    apiVersion: ORDER_VALIDATE_API_VERSION,
    body,
    controller,
    flags,
    // for this endpoint, we want to failure modes like a valid promo that reduce the
    // total order amount to below the minimum produce a error code but should still update
    // the local state (e.g. the promo code applied)
    returnDataForErrorResponse: true,
  });
}

export async function validateOrder(
  body: Record<string, unknown>,
  { controller, flags }: OrderApiCommonOptions = {}
) {
  return postToChownowApi<OrderValidateResponseV6>({
    endpoint: 'order/validate',
    apiVersion: ORDER_VALIDATE_API_VERSION,
    body,
    controller,
    flags,
    // for this endpoint, we want to failure modes like a valid promo that reduce the
    // total order amount to below the minimum produce a error code but should still update
    // the local state (e.g. the promo code applied)
    returnDataForErrorResponse: true,
  });
}

export function normalizeOrderResponse(
  response: OrderValidateResponseV6 | OrderDetailResponseV6
): Omit<LocalOrderType, 'items' | 'restaurant_id' | 'when'> {
  // omit items
  const { items: _items, ...rest } = response;
  // TODO: remove 'tip' when we switch exclusively to /validate + POST /order v6:
  // https://chownow.atlassian.net/browse/CN-35607, https://chownow.atlassian.net/browse/CN-35606
  const { tip } = response as { tip?: string };

  return {
    ...rest,
    tip: tip ? parseFloat(tip) : undefined,
  };
}

export async function getYelpOpportunity(opportunityToken: string) {
  return getFromChownowApi<YelpOpportunityResponse>({
    endpoint: `yelp/opportunity/${opportunityToken}`,
  });
}
